/**
 * Warm the browser cache for public images/fonts while the email gate is shown.
 * Safe to call multiple times (dedupes by URL).
 *
 * Keep WEDDING_PRELOAD_IMAGE_URLS in sync with image files in public/assets/
 * (plus any other /public images the post-gate app loads by URL).
 */

const injectedPreloads = new Set();
const prefetchedImages = new Set();

export function resolvePublicAsset(path) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${normalized}` : normalized;
}

/** Every img/CSS url under public/assets/ used after unlock (see App.js `sectionArt` + modals). */
export const WEDDING_PRELOAD_IMAGE_URLS = [
  '/assets/before-we-panic.svg',
  '/assets/blue-paper.png',
  '/assets/divider.svg',
  '/assets/faq-marker-stay-accent.svg',
  '/assets/faq-marker.svg',
  '/assets/faq-title.svg',
  '/assets/footer.png',
  '/assets/hero-couple.png',
  '/assets/hoofy.svg',
  '/assets/hotels.svg',
  '/assets/hover-flower.svg',
  '/assets/join-us-on.svg',
  '/assets/mb-monogram.png',
  '/assets/new-years-eve.svg',
  '/assets/notebook-paper.png',
  '/assets/painter-tape.png',
  '/assets/paper-texture.jpg',
  '/assets/penicuik-courtyard.jpg',
  '/assets/plane.svg',
  '/assets/please-rsvp.svg',
  '/assets/please.svg',
  '/assets/red_paper.png',
  '/assets/scroll-flower.svg',
  '/assets/splitter.svg',
  '/assets/stay.svg',
  '/assets/the-ceremony.svg',
  '/assets/the-reception.svg',
  '/assets/thumbnail-strip.gif',
  '/assets/to-celebrate.svg',
  '/assets/todo.svg',
  '/assets/train.svg',
  '/assets/travel.svg',
  '/assets/VIP-ticket.png',
  '/assets/we-hope-to-see-you-in-Scotland-x.svg',
  '/assets/wedding-coaches.svg',
  '/assets/withlove.svg',
].map(resolvePublicAsset);

/** Public fonts (match files in public/fonts). */
export const WEDDING_PRELOAD_FONTS = [
  { path: '/fonts/GT-Alpina-Typewriter-Regular.woff2', type: 'font/woff2' },
  { path: '/fonts/GT-Alpina-Typewriter-Regular.woff', type: 'font/woff' },
].map(({ path, type }) => ({ href: resolvePublicAsset(path), type }));

function injectLinkPreload(href, as, type, crossOrigin) {
  if (typeof document === 'undefined' || injectedPreloads.has(href)) return;
  injectedPreloads.add(href);
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = href;
  link.as = as;
  if (type) link.type = type;
  if (crossOrigin) link.crossOrigin = crossOrigin;
  document.head.appendChild(link);
}

function warmImage(href) {
  if (typeof Image === 'undefined' || prefetchedImages.has(href)) return;
  prefetchedImages.add(href);
  const img = new Image();
  img.src = href;
}

/**
 * Starts preloading as soon as called (after first paint when invoked from useEffect).
 * Dedupes; safe to call multiple times.
 */
export function preloadWeddingSiteAssets() {
  if (typeof window === 'undefined') return;

  for (const href of WEDDING_PRELOAD_IMAGE_URLS) {
    const ext = href.split('.').pop()?.split('?')[0]?.toLowerCase();
    const mime =
      ext === 'svg'
        ? 'image/svg+xml'
        : ext === 'png'
          ? 'image/png'
          : ext === 'gif'
            ? 'image/gif'
            : ext === 'jpg' || ext === 'jpeg'
              ? 'image/jpeg'
              : ext === 'webp'
                ? 'image/webp'
                : undefined;
    injectLinkPreload(href, 'image', mime, false);
    warmImage(href);
  }

  for (const { href, type } of WEDDING_PRELOAD_FONTS) {
    injectLinkPreload(href, 'font', type, 'anonymous');
  }
}
